import { Component, OnInit } from "@angular/core";

@Component({
    selector: 'app-gantt-echart',
    templateUrl: './gantt-echart.component.html',
    styleUrls: ['./gantt-echart.component.scss']
})
export class GanttChartComponent implements OnInit {

    chartOption: any = {};

    ngOnInit() {
        // Define threshold value (e.g., max hours allowed per day)
        const thresholdValue = 8;

        // Sample data: activity with daily values
        const activities = [
            {
                name: 'Activity A',
                dailyData: [
                    { date: '2024-01-01', value: 25 },
                ]
            },
            {
                name: 'Activity B',
                dailyData: [
                    { date: '2024-01-01', value: 7 },
                    { date: '2024-01-02', value: 8 },
                    { date: '2024-01-03', value: 6 },
                    { date: '2024-01-04', value: 11 }, // Breach
                    { date: '2024-01-05', value: 7 },
                ]
            },
            {
                name: 'Activity C',
                dailyData: [
                    { date: '2024-01-01', value: 5 },
                    { date: '2024-01-02', value: 9 },  // Breach
                    { date: '2024-01-03', value: 7 },
                    { date: '2024-01-04', value: 6 },
                    { date: '2024-01-05', value: 8 },
                ]
            },
            {
                name: 'Activity D',
                dailyData: [
                    { date: '2024-01-01', value: 8 },
                    { date: '2024-01-02', value: 7 },
                    { date: '2024-01-03', value: 12 }, // Breach
                    { date: '2024-01-04', value: 9 },  // Breach
                    { date: '2024-01-05', value: 6 },
                ]
            }
        ];

        // Flatten data for rendering - create both green and red portions
        const greenData: any[] = [];
        const redData: any[] = [];
        let globalIndex = 0;

        activities.forEach((activity, activityIndex) => {
            activity.dailyData.forEach((day, dayIndex) => {
                const breachesThreshold = day.value > thresholdValue;
                const greenValue = Math.min(day.value, thresholdValue);
                const redValue = breachesThreshold ? day.value - thresholdValue : 0;

                // Green portion (up to threshold)
                greenData.push({
                    name: activity.name,
                    value: [
                        activityIndex,
                        new Date(day.date).getTime(),
                        greenValue,
                        day.value, // Store full value for tooltip
                        dayIndex // Store day index
                    ],
                    itemStyle: {
                        color: '#27ae60'
                    }
                });

                // Red portion (breach part) - always add for alignment
                redData.push({
                    name: activity.name,
                    value: [
                        activityIndex,
                        new Date(day.date).getTime(),
                        redValue,
                        day.value, // Store full value for tooltip
                        greenValue, // Store offset for stacking
                        dayIndex // Store day index
                    ],
                    itemStyle: {
                        color: '#e74c3c'
                    }
                });

                globalIndex++;
            });
        });

        this.chartOption = {
            // title: {
            //     text: 'Daily Activity Metrics with Threshold Breach (Horizontal)',
            //     left: 'center'
            // },
            tooltip: {
                formatter: (params: any) => {
                    const date = new Date(params.value[1]).toLocaleDateString();
                    const totalValue = params.value[3];
                    const status = totalValue > thresholdValue ? 'BREACH' : 'Normal';
                    return `${params.name}<br/>Date: ${date}<br/>Total Value: ${totalValue} hours<br/>Threshold: ${thresholdValue} hours<br/>Status: ${status}`;
                }
            },
            // legend: {
            //     data: ['Within Threshold (Green)', 'Breach Portion (Red)'],
            //     bottom: 10
            // },
            grid: {
                left: '8%',
                right: '3%',
                top: '12%',
                bottom: '10%',
                containLabel: true
            },
            xAxis: {
                type: 'value',
                show: false,
                min: 0,
                max: 500 // Increased for wider bars and spacing
            },
            yAxis: {
                type: 'category',
                data: activities.map(a => a.name),
                inverse: true,
                axisLine: { show: false },
                axisTick: { show: false }
            },
            series: [
                // Green portion (up to threshold)
                {
                    type: 'custom',
                    name: 'Within Threshold (Green)',
                    renderItem: (params: any, api: any) => {
                        const categoryIndex = api.value(0);
                        const greenValue = api.value(2);
                        const totalValue = api.value(3);
                        const dayIndex = api.value(4);

                        // Get Y position for the activity
                        const yAxisPoint = api.coord([0, categoryIndex]);

                        const barHeight = 30;
                        const barGap = 12;
                        const maxBarWidth = 80;
                        const borderRadius = 4;

                        // Calculate horizontal offset based on day index
                        const xOffset = dayIndex * (maxBarWidth + barGap);

                        // Scale total bar width based on total value (assuming max value is ~15)
                        const totalBarWidth = (totalValue / 15) * maxBarWidth;

                        // Calculate actual bar width based on green value proportion of total
                        const greenProportion = greenValue / totalValue;
                        const barWidth = greenProportion * totalBarWidth;

                        const hasRedPortion = totalValue > thresholdValue;

                        return {
                            type: 'group',
                            children: [
                                {
                                    type: 'rect',
                                    shape: {
                                        x: api.coord([0, categoryIndex])[0] + xOffset,
                                        y: yAxisPoint[1] - barHeight / 2,
                                        width: barWidth,
                                        height: barHeight,
                                        r: hasRedPortion ? [borderRadius, 0, 0, borderRadius] : borderRadius
                                    },
                                    style: api.style()
                                },
                                {
                                    type: 'text',
                                    style: {
                                        text: greenValue.toString(),
                                        x: api.coord([0, categoryIndex])[0] + xOffset + barWidth / 2,
                                        y: yAxisPoint[1],
                                        fill: '#fff',
                                        fontSize: 10,
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        textVerticalAlign: 'middle'
                                    }
                                }
                            ]
                        };
                    },
                    encode: {
                        y: 0
                    },
                    data: greenData,
                    z: 1
                },
                // Red portion (breach - directly after green, no gap)
                {
                    type: 'custom',
                    name: 'Breach Portion (Red)',
                    renderItem: (params: any, api: any) => {
                        const categoryIndex = api.value(0);
                        const redValue = api.value(2);
                        const totalValue = api.value(3);
                        const greenValue = api.value(4);
                        const dayIndex = api.value(5);

                        const yAxisPoint = api.coord([0, categoryIndex]);

                        const barHeight = 30;
                        const barGap = 12;
                        const maxBarWidth = 80;
                        const borderRadius = 4;

                        const xOffset = dayIndex * (maxBarWidth + barGap);

                        // Scale total bar width based on total value
                        const totalBarWidth = (totalValue / 15) * maxBarWidth;

                        // Calculate proportions
                        const greenProportion = greenValue / totalValue;
                        const redProportion = redValue / totalValue;

                        const greenBarWidth = greenProportion * totalBarWidth;
                        const redBarWidth = redProportion * totalBarWidth;

                        // Only render if there's a breach
                        if (redValue === 0) {
                            return null;
                        }

                        return {
                            type: 'group',
                            children: [
                                {
                                    type: 'rect',
                                    shape: {
                                        x: api.coord([0, categoryIndex])[0] + xOffset + greenBarWidth,
                                        y: yAxisPoint[1] - barHeight / 2,
                                        width: redBarWidth,
                                        height: barHeight,
                                        r: [0, borderRadius, borderRadius, 0]
                                    },
                                    style: api.style()
                                },
                                {
                                    type: 'text',
                                    style: {
                                        text: redValue.toString(),
                                        x: api.coord([0, categoryIndex])[0] + xOffset + greenBarWidth + redBarWidth / 2,
                                        y: yAxisPoint[1],
                                        fill: '#fff',
                                        fontSize: 10,
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        textVerticalAlign: 'middle'
                                    }
                                }
                            ]
                        };
                    },
                    encode: {
                        y: 0
                    },
                    data: redData,
                    z: 2
                },
                // Labels showing dates below bars
                {
                    type: 'custom',
                    name: 'Date Labels',
                    renderItem: (params: any, api: any) => {
                        const categoryIndex = api.value(0);
                        const date = api.value(1);
                        const dayIndex = api.value(4);

                        const yAxisPoint = api.coord([0, categoryIndex]);

                        const barHeight = 30;
                        const barGap = 12;
                        const totalBarWidth = 80;

                        const xOffset = dayIndex * (totalBarWidth + barGap);

                        const dateStr = new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

                        return {
                            type: 'text',
                            style: {
                                text: dateStr,
                                x: api.coord([0, categoryIndex])[0] + xOffset + totalBarWidth / 2,
                                y: yAxisPoint[1] + barHeight / 2 + 12,
                                fill: '#666',
                                fontSize: 9,
                                textAlign: 'center'
                            }
                        };
                    },
                    encode: {
                        y: 0
                    },
                    data: greenData,
                    z: 3
                }
            ],
            // Add vertical threshold reference line
            graphic: [
                {
                    type: 'line',
                    shape: {
                        x1: 0,
                        y1: 0,
                        x2: 0,
                        y2: 0
                    },
                    style: {
                        stroke: '#e74c3c',
                        lineWidth: 2,
                        lineDash: [5, 5]
                    },
                    silent: true,
                    z: 0
                }
            ]
        };
    }
}

